/**
 * Opening a document on Teslyn Cloud's own address (R235, docs/specs/cloud/document-viewer.md). The tab opens at once
 * inside the click, and the viewer page fetches the file with the person's own sign-in, so there is no storage address
 * to hand over.
 */
import { documentPageSearch, type DocumentRef } from '@teslyn/domain';

/** The portal's address of a document, on this address. */
export function documentUrl(ref: DocumentRef): string {
  return `/${documentPageSearch(ref, window.location.search)}`;
}

/** Teslyn Cloud added to a home screen has no tabs: a new one would leave the app, and its sign-in, behind. */
function installed(): boolean {
  const ios = (window.navigator as { standalone?: boolean }).standalone === true;
  return ios || window.matchMedia?.('(display-mode: standalone)').matches === true;
}

/** A signed storage link made with `{ download }`: the browser saves the file and stays on the page. */
const savesFile = (url: string): boolean =>
  /\/storage\/v1\/object\/sign\/[^?]*\?(?:[^#]*&)?download=/.test(url);

/**
 * Every address Teslyn Cloud opens from a button goes through here: its own pages, another site (a share link, the
 * signing page), and a file to save. A file to save needs no tab, so none can be left showing its storage address.
 * Anything else opens in a new tab, else in this one when the browser blocks the tab or the app has none.
 *
 * Never `noopener`: `window.open` then always answers null, which reads as a blocked tab, so a "no tab" fallback also
 * moves this page, or fetches a file twice. The tab opens blank so its `opener` can be cleared before it reaches
 * another site, which `noopener` was there for.
 */
export function openInNewTab(url: string): void {
  if (savesFile(url) || installed()) {
    window.location.assign(url);
    return;
  }
  const tab = window.open('', '_blank');
  if (tab) {
    tab.opener = null;
    tab.location.href = url;
  } else window.location.assign(url);
}

/**
 * Close the viewer. A browser only lets a page close a tab a page opened, so a link pasted or opened from an email
 * goes back, or to the portal.
 */
export function closeDocument(): void {
  window.close();
  window.setTimeout(() => {
    if (window.history.length > 1) window.history.back();
    else {
      const tenant = new URLSearchParams(window.location.search).get('tenant');
      window.location.assign(tenant ? `/?tenant=${encodeURIComponent(tenant)}` : '/');
    }
  }, 200);
}
